import { z } from "zod";
import { memoryApplicabilitySchema, memoryVersionIdSchema, } from "./memories.js";
import { memoryRolloutModeSchema } from "./memory-policy.js";
import { intentionalMemoryDigestPacketSchema } from "./memory-packet.js";
// Serving identifiers are emitted into memory-trace receipts, whose stable
// identifier contract is bounded to 240 characters.
const servingIdentifierSchema = z.string().trim().min(1).max(240);
const servingRunIdentifierSchema = z.string().trim().min(1).max(225).refine((value) => !value.startsWith("unverified-run:"), { message: "Run ids cannot collide with the unverified actor sentinel" });
const applicabilityIdentifierSchema = z.string().trim().min(1).max(500);
const servingTimestampSchema = z.number().int().nonnegative().safe();
export const MEMORY_LIFECYCLE_TARGET_OPERATION = "memory_lifecycle";
const servingDimensionSchema = z.enum([
    "databaseIds",
    "datasetIds",
    "entityTypes",
    "entityIds",
    "schemaFingerprints",
    "metrics",
    "operations",
    "memoryRule",
]);
const analysisValuesSchema = z.array(applicabilityIdentifierSchema).max(100);
/** Server-owned interpretation of the persisted run request. */
export const memoryServingRequestAnalysisSchema = z.object({
    tenantSlug: servingIdentifierSchema,
    organisationId: servingIdentifierSchema.nullable(),
    authoritativeActorId: servingIdentifierSchema.nullable(),
    sessionId: servingIdentifierSchema.nullable(),
    channelId: servingIdentifierSchema.nullable(),
    databaseIds: analysisValuesSchema,
    datasetIds: analysisValuesSchema,
    entityTypes: analysisValuesSchema,
    entityIds: analysisValuesSchema,
    schemaFingerprints: analysisValuesSchema,
    metrics: analysisValuesSchema,
    operations: analysisValuesSchema,
    currentTurnOverrides: memoryApplicabilitySchema,
    selectedMemoryVersionIds: z.array(memoryVersionIdSchema).max(25),
    ignoredMemoryVersionIds: z.array(memoryVersionIdSchema).max(25),
    memoryUseSuppressionReason: z.enum([
        "unsupported_requested_valid_time",
    ]).nullable().default(null),
}).strict();
export const canonicalEpisodeMemoryMatchSchema = z.object({
    episodeId: servingIdentifierSchema,
    sourceRunId: servingIdentifierSchema,
    sourceSessionId: servingIdentifierSchema,
    snippet: z.string().trim().min(1).max(2_000),
    finishedAt: servingTimestampSchema,
    score: z.number().finite().nonnegative(),
}).strict();
export const memoryServingClarificationChoiceSchema = z.object({
    choiceId: servingIdentifierSchema,
    value: z.string().trim().min(1).max(500),
    label: z.string().trim().min(1).max(500),
}).strict();
export const memoryServingClarificationSchema = z.object({
    question: z.string().trim().min(1).max(2_000),
    dimension: servingDimensionSchema,
    choices: z.array(memoryServingClarificationChoiceSchema).min(2).max(12),
    referencedMemoryVersionIds: z.array(memoryVersionIdSchema).min(1).max(25),
}).strict();
const memoryServingResponseBaseSchema = z.object({
    schemaVersion: z.literal(1),
    attemptId: servingIdentifierSchema,
    runId: servingRunIdentifierSchema,
    asOf: servingTimestampSchema,
    effectiveRolloutMode: memoryRolloutModeSchema,
    requestAnalysis: memoryServingRequestAnalysisSchema,
    technicalFailure: z.discriminatedUnion("code", [
        z.object({
            code: z.literal("memory_serving_timeout"),
            stage: z.literal("retrieval"),
            timeoutMs: z.number().int().positive().max(30_000),
        }).strict(),
        z.object({
            code: z.literal("memory_candidate_overflow"),
            lane: z.enum(["intentional", "episodic"]),
            stage: z.enum(["retrieval", "packet", "clarification"]),
            limit: z.number().int().positive().max(100),
            observedCount: z.number().int().positive().max(101),
        }).strict().refine((failure) => failure.observedCount > failure.limit, { message: "Candidate overflow must exceed its declared limit" }),
    ]).nullable().optional(),
});
const admittedMemoryServingResponseSchema = memoryServingResponseBaseSchema.extend({
    outcome: z.literal("admitted"),
    intentionalPacket: intentionalMemoryDigestPacketSchema,
    episodes: z.array(canonicalEpisodeMemoryMatchSchema).max(8),
    clarification: z.null(),
}).strict().refine((response) => response.intentionalPacket.items.length > 0 || response.episodes.length > 0, { message: "An admitted serving response must contain at least one memory" });
const noMemoryServingResponseSchema = memoryServingResponseBaseSchema.extend({
    outcome: z.literal("no_memory"),
    intentionalPacket: intentionalMemoryDigestPacketSchema,
    episodes: z.tuple([]),
    clarification: z.null(),
}).strict().refine((response) => response.intentionalPacket.items.length === 0, { message: "A no-memory response must contain an empty intentional packet" });
const clarificationMemoryServingResponseSchema = memoryServingResponseBaseSchema.extend({
    outcome: z.literal("clarification_required"),
    intentionalPacket: intentionalMemoryDigestPacketSchema,
    episodes: z.array(canonicalEpisodeMemoryMatchSchema).max(8),
    clarification: memoryServingClarificationSchema,
}).strict();
export const workerMemoryServingResponseSchema = z.discriminatedUnion("outcome", [
    admittedMemoryServingResponseSchema,
    noMemoryServingResponseSchema,
    clarificationMemoryServingResponseSchema,
]).superRefine((response, context) => {
    const expectedAttemptId = `memory-serving:${response.runId}`;
    if (response.attemptId !== expectedAttemptId) {
        context.addIssue({
            code: "custom",
            path: ["attemptId"],
            message: "attemptId must be derived from the authoritative run id",
        });
    }
    if (response.intentionalPacket.packetId !== response.attemptId) {
        context.addIssue({
            code: "custom",
            path: ["intentionalPacket", "packetId"],
            message: "The intentional packet must belong to this serving attempt",
        });
    }
    if (response.technicalFailure && response.outcome !== "no_memory") {
        context.addIssue({
            code: "custom",
            path: ["technicalFailure"],
            message: "A technical failure must fail closed to a no-memory response",
        });
    }
    const expectedActorId = response.requestAnalysis.authoritativeActorId ??
        `unverified-run:${response.runId}`;
    const packetScope = response.intentionalPacket.scope;
    if (packetScope.tenantSlug !== response.requestAnalysis.tenantSlug ||
        packetScope.organisationId !== response.requestAnalysis.organisationId ||
        packetScope.actorId !== expectedActorId ||
        packetScope.channelId !== response.requestAnalysis.channelId) {
        context.addIssue({
            code: "custom",
            path: ["intentionalPacket", "scope"],
            message: "The packet scope must equal the authoritative request analysis scope",
        });
    }
    const selected = response.requestAnalysis.selectedMemoryVersionIds;
    const ignored = response.requestAnalysis.ignoredMemoryVersionIds;
    if (new Set(selected).size !== selected.length) {
        context.addIssue({ code: "custom", path: ["requestAnalysis", "selectedMemoryVersionIds"], message: "Selected memory versions must be unique" });
    }
    if (new Set(ignored).size !== ignored.length) {
        context.addIssue({ code: "custom", path: ["requestAnalysis", "ignoredMemoryVersionIds"], message: "Ignored memory versions must be unique" });
    }
    if (selected.some((versionId) => ignored.includes(versionId))) {
        context.addIssue({ code: "custom", path: ["requestAnalysis"], message: "Selected and ignored memory versions must be disjoint" });
    }
    const episodeIds = response.episodes.map((episode) => episode.episodeId);
    const episodeRunIds = response.episodes.map((episode) => episode.sourceRunId);
    if (new Set(episodeIds).size !== episodeIds.length ||
        new Set(episodeRunIds).size !== episodeRunIds.length) {
        context.addIssue({ code: "custom", path: ["episodes"], message: "Episode and source-run references must be unique" });
    }
    if (response.episodes.some((episode) => episode.finishedAt > response.asOf)) {
        context.addIssue({ code: "custom", path: ["episodes"], message: "Episodes cannot postdate the serving watermark" });
    }
    if (response.outcome !== "clarification_required")
        return;
    const choiceIds = response.clarification.choices.map((choice) => choice.choiceId);
    const choiceValues = response.clarification.choices.map((choice) => choice.value);
    const refs = response.clarification.referencedMemoryVersionIds;
    const packetRefs = new Set(response.intentionalPacket.items.map((item) => item.versionId));
    if (new Set(choiceIds).size !== choiceIds.length ||
        new Set(choiceValues).size !== choiceValues.length) {
        context.addIssue({ code: "custom", path: ["clarification", "choices"], message: "Clarification choices must be unique" });
    }
    if (new Set(refs).size !== refs.length || refs.some((versionId) => !packetRefs.has(versionId))) {
        context.addIssue({ code: "custom", path: ["clarification", "referencedMemoryVersionIds"], message: "Clarification references must be unique candidates in the packet" });
    }
});
/** Deliberately excludes prompt and scope: both are read from task_runs. */
export const workerMemoryServingRequestSchema = z.object({
    schemaVersion: z.literal(1),
    runId: servingRunIdentifierSchema,
    tenantSlug: servingIdentifierSchema,
}).strict();
